/**
 * EstimateCreateModal - Modal for creating new estimates
 */

import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Plus, UserPlus } from "phosphor-react";
import LineItemsEditor from "../LineItemsEditor";
import TotalsSummary from "../TotalsSummary";
import CustomerCreateModal from "../CustomerCreateModal";
import SearchableCustomerSelect from "../SearchableCustomerSelect";
import DepositConfig from "./DepositConfig";

export default function EstimateCreateModal({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  customers,
  taxRatePercent,
  onSubmit,
  onUpdateLineItem,
  onAddLineItem,
  onRemoveLineItem,
  onCatalogSelect,
  onFormatPrice,
  onReset,
  onCustomerCreated,
  validationErrors = [],
}) {
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);

  // Calculate estimate total for deposit preview
  const estimateTotal = useMemo(() => {
    if (!formData?.items) return 0;
    const subtotal = formData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.price) || 0;
      return sum + qty * price;
    }, 0);
    const taxableTotal = formData.items.reduce((sum, item) => {
      if (!item.is_taxable) return sum;
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.price) || 0;
      return sum + qty * price;
    }, 0);
    const tax = taxableTotal * (taxRatePercent / 100);
    return subtotal + tax;
  }, [formData?.items, taxRatePercent]);

  const handleCustomerCreated = (customer) => {
    // Add to customers list and select it
    if (onCustomerCreated) {
      onCustomerCreated(customer);
    }
    setFormData({ ...formData, customer_id: customer.customer_id });
  };

  // Handle deposit config changes
  const handleDepositChange = (depositData) => {
    setFormData({
      ...formData,
      deposit_enabled: depositData.deposit_enabled,
      deposit_type: depositData.deposit_type,
      deposit_value: depositData.deposit_value,
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <Button
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
            data-testid="add-estimate-btn"
          >
            <Plus size={18} className="mr-2" />
            New Estimate
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl" data-testid="estimate-create-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              Create Estimate
            </DialogTitle>
          </DialogHeader>
          
          <DialogBody>
            <form id="estimate-create-form" onSubmit={onSubmit} className="space-y-4">
              {/* Top row: Customer + Title - responsive layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between h-5 mb-1">
                    <span className="text-sm font-medium">Customer *</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-5 px-2 text-xs text-[#FF6600] hover:text-[#CC5200] hover:bg-orange-50"
                      onClick={() => setShowQuickCustomer(true)}
                      data-testid="estimate-new-customer-btn"
                    >
                      <UserPlus size={12} className="mr-1" />
                      New
                    </Button>
                  </div>
                  <SearchableCustomerSelect
                    customers={customers}
                    value={formData.customer_id}
                    onChange={(v) => setFormData({ ...formData, customer_id: v })}
                    label=""
                    data-testid="estimate-customer-select"
                  />
                </div>
                <div>
                  <div className="h-5 mb-1">
                    <Label className="text-sm font-medium">Title *</Label>
                  </div>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Kitchen renovation estimate"
                    className="h-12 border-2"
                    required
                    data-testid="estimate-title-input"
                  />
                </div>
              </div>

              <LineItemsEditor
                items={formData.items}
                onUpdateItem={onUpdateLineItem}
                onAddItem={onAddLineItem}
                onRemoveItem={onRemoveLineItem}
                onCatalogSelect={onCatalogSelect}
                onFormatPrice={onFormatPrice}
                taxRatePercent={taxRatePercent}
                testIdPrefix="estimate-line"
                validationErrors={validationErrors}
              />

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Terms, conditions, or additional notes..."
                  className="mt-1 border-2"
                  rows={3}
                />
              </div>

              <TotalsSummary items={formData.items} taxRatePercent={taxRatePercent} />

              {/* Deposit Configuration - secondary config section */}
              <DepositConfig
                depositEnabled={formData.deposit_enabled || false}
                depositType={formData.deposit_type}
                depositValue={formData.deposit_value}
                onChange={handleDepositChange}
                estimateTotal={estimateTotal}
                testIdPrefix="estimate-deposit"
              />
            </form>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onReset();
              }}
              className="flex-1 sm:flex-none border-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="estimate-create-form"
              className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
              disabled={!formData.customer_id || !formData.title}
              data-testid="save-estimate-btn"
            >
              Create Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Create Modal */}
      <CustomerCreateModal
        isOpen={showQuickCustomer}
        onClose={() => setShowQuickCustomer(false)}
        onCustomerCreated={handleCustomerCreated}
      />
    </>
  );
}
